import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import { StrategyCard } from '../components/blocks/StrategyCard';
import { Chip } from '../components/primitives/Chip';
import { Container } from '../components/primitives/Container';
import { SectionHead } from '../components/primitives/SectionHead';
import { useContent } from '../content/load';

const Filters = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  padding: ${(p) => p.theme.step[4]}px 0;
`;

const FilterButton = styled.button<{ $active?: boolean }>`
  background: ${(p) => (p.$active ? p.theme.color.accent.amber : 'transparent')};
  color: ${(p) => (p.$active ? p.theme.color.bg[0] : p.theme.color.text[1])};
  border: 1px solid
    ${(p) => (p.$active ? p.theme.color.accent.amber : p.theme.color.border.med)};
  padding: 6px 12px;
  border-radius: 999px;
  font-size: ${(p) => p.theme.step[1]}px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: ${(p) => p.theme.step[5]}px;
  padding-bottom: ${(p) => p.theme.step[8]}px;
`;

export default function Marketplace() {
  const { strategies } = useContent();
  const [params, setParams] = useSearchParams();
  const cat = params.get('category');
  const tier = params.get('tier');

  const categories = useMemo(
    () => Array.from(new Set(strategies.map((s) => s.category))).sort(),
    [strategies],
  );
  const tiers = useMemo(() => Array.from(new Set(strategies.map((s) => s.tier))).sort(), [
    strategies,
  ]);

  const filtered = strategies.filter(
    (s) => (!cat || s.category === cat) && (!tier || s.tier === tier),
  );

  const setParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  return (
    <Container>
      <SectionHead
        eyebrow="STRATEGIES"
        heading="The full book."
        body={`${strategies.length} strategies across ${tiers.length} tiers.`}
      />

      <Filters>
        <FilterButton $active={!cat} onClick={() => setParam('category', null)}>
          All categories
        </FilterButton>
        {categories.map((c) => (
          <FilterButton key={c} $active={cat === c} onClick={() => setParam('category', c)}>
            {c}
          </FilterButton>
        ))}
      </Filters>

      <Filters>
        <FilterButton $active={!tier} onClick={() => setParam('tier', null)}>
          All tiers
        </FilterButton>
        {tiers.map((t) => (
          <FilterButton key={t} $active={tier === t} onClick={() => setParam('tier', t)}>
            <Chip $variant="amber">{t}</Chip>
          </FilterButton>
        ))}
      </Filters>

      <Grid>
        {filtered.map((s) => (
          <StrategyCard key={s.id} strategy={s} />
        ))}
        {filtered.length === 0 && (
          <p style={{ opacity: 0.7 }}>No strategies match these filters.</p>
        )}
      </Grid>
    </Container>
  );
}
